// src/hooks/useOAuthCallback.ts
import { useEffect, useCallback, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, AppUser } from '../contexts/AuthContext';
import apiClient from '../services/api';
import axios from 'axios';
import logger from '../utils/logger';

interface UseOAuthCallbackProps {
  providerName: string; // e.g., 'GitHub', 'Google'
  apiCallbackPath: string; // e.g., '/auth/google/callback', '/google/callback'
}

interface OAuthCallbackResult {
  isLoading: boolean;
  errorMsg: string | null;
}

export const useOAuthCallback = ({
  providerName,
  apiCallbackPath,
}: UseOAuthCallbackProps): OAuthCallbackResult => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const effectRan = useRef(false); // Ref to track if the effect logic has executed

  // Get the code and state once.
  const code = searchParams.get('code');
  const stateParam = searchParams.get('state');
  const errorParam = searchParams.get('error'); // Provider might return error this way

  // Parse the state parameter to get the original redirect path
  let redirectTo = '/'; // Default redirect path
  if (stateParam) {
    try {
      const decodedState = JSON.parse(decodeURIComponent(stateParam));
      redirectTo = decodedState.redirectTo || '/';
      // Optional: Verify provider if needed
      // if (decodedState.provider !== providerName) { ... }
    } catch (e) {
      logger.error(`${providerName}Callback: Failed to parse state parameter:`, e);
      // Setting error state immediately if state is crucial and invalid
      // useEffect will still run, but this provides earlier feedback
      // setErrorMsg("Invalid state parameter received during authentication.");
      // setIsLoading(false);
    }
  }

  // Wrap exchangeCodeForToken in useCallback to prevent dupilcate loading
  const exchangeCodeForToken = useCallback(async (codeToExchange: string) => {
    try {
      logger.debug(`${providerName}Callback: exchanging code for token with code: ${codeToExchange}`);
      // Use the provided apiCallbackPath
      const response = await apiClient.get(`${apiCallbackPath}?code=${codeToExchange}`);

      // --- IMPORTANT: Standardize Backend Response ---
      // backend should return a consistent user object structure
      // regardless of the provider. Example structure:
      const { username, id, access_token, email, name, picture } = response.data;
      const finalUsername = name || username || email; // Determine best username source

      if (!id || !finalUsername || !access_token) {
         logger.error(`${providerName}Callback: Invalid data received from backend`, response.data);
         logger.error( `flags: id: ${!id}, username: ${!finalUsername}, access_token: ${!access_token}`);
         throw new Error("Incomplete user information received from server.");
      }

      logger.info(`${providerName}Callback: got API response: User=${finalUsername}, ID=${id}`);
      // Pass the standardized user data to signIn
      // ToDo: also retain email that was received
      // TBD: how to handle the "access_token" .. the new AppUser structured does not have that field
      const newUserData: AppUser = {
          uid: id,
          firebaseUser: undefined, // since this is a manually contsructed auth object
          name: finalUsername,
          email: email,
          avatarUrl: picture,
          authProvider: "custom", // indicate if it is github or google or other
          workspaces: [] // None concretely available 
      }        

      signIn( newUserData);

      logger.debug(`${providerName}Callback: Sign in complete, navigating to: ${redirectTo}`);
      navigate(redirectTo, { replace: true }); // Use replace to avoid callback in history

    } catch (error) {
      logger.error(`${providerName}Callback: Error exchanging code for token:`, error);
      if (axios.isAxiosError(error) && error.response) {
        setErrorMsg(`Failed to sign in: API Error ${error.response.status} - ${error.response.data?.message || error.message}`);
      } else if (error instanceof Error) {
        setErrorMsg(`Failed to sign in: ${error.message}`);
      } else {
        setErrorMsg(`Failed to sign in: An unknown error occurred.`);
      }
      setIsLoading(false);
    }
  }, [signIn, navigate, redirectTo, providerName, apiCallbackPath]); // Add hook params to dependencies

  useEffect(() => {
    logger.debug(`${providerName}Callback: useEffect triggered. Code: ${code}, Error: ${errorParam}, EffectRan: ${effectRan.current}`);

    // Check for errors returned directly from the OAuth provider
    if (errorParam) {
      logger.error(`${providerName}Callback: Error returned from ${providerName}: ${errorParam}`);
      setErrorMsg(`Authentication failed: ${errorParam}`);
      setIsLoading(false);
      effectRan.current = true; // Mark as run
      return; // Stop processing
    }

    // Check if the effect has already run or if we don't have a code
    if (effectRan.current || !code) {
      if (!code && !effectRan.current && !errorParam) {
        logger.error(`${providerName}Callback: No authorization code found in URL.`);
        setErrorMsg('Authentication failed: No authorization code provided.');
        setIsLoading(false);
        effectRan.current = true;
      } else if (effectRan.current) {
        logger.debug(`${providerName}Callback: Skipping effect run because it already ran.`);
      }
      return; // Exit the effect
    }

    // --- Proceed with exchange ---
    setIsLoading(true);
    setErrorMsg(null);
    logger.debug(`${providerName}Callback: Calling exchangeCodeForToken...`);
    exchangeCodeForToken(code);

    // Mark that the effect's main logic has run
    effectRan.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only ONCE per logical mount

  return { isLoading, errorMsg };
};
